/**
 * Admin Layout Component for Re:Nav-96
 *
 * Provides the layout structure for authenticated super admin pages.
 * Includes a sidebar navigation for admin-specific features like
 * content management, photo approval, and survey builder.
 *
 * @example
 * // In router configuration
 * {
 *   path: 'admin',
 *   element: (
 *     <ProtectedRoute requiredRole="super_admin">
 *       <AdminLayout />
 *     </ProtectedRoute>
 *   ),
 *   children: [
 *     { index: true, element: <AdminDashboardPage /> },
 *     { path: 'content', element: <ContentEditorPage /> },
 *     { path: 'photos', element: <PhotoApprovalPage /> },
 *   ]
 * }
 */

import { Outlet, NavLink, useLocation, Link } from 'react-router';
import { useAuth } from '@/hooks/useAuth';

/**
 * Navigation item interface
 */
interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
}

/**
 * Navigation items for the admin sidebar
 */
const navItems: NavItem[] = [
  {
    to: '/admin',
    label: 'Dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
    ),
  },
  {
    to: '/admin/registrations',
    label: 'Registrations',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
        />
      </svg>
    ),
  },
  {
    to: '/admin/members',
    label: 'Members',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
  },
  {
    to: '/admin/groups',
    label: 'Groups',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
        />
      </svg>
    ),
  },
  {
    to: '/admin/content',
    label: 'Content',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
        />
      </svg>
    ),
  },
  {
    to: '/admin/photos',
    label: 'Photos',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    to: '/admin/surveys/new',
    label: 'Surveys',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
        />
      </svg>
    ),
  },
  // spec-140: Projects nav entry removed. Tasks live under Groups now.
  {
    to: '/admin/events',
    label: 'Events',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    to: '/admin/gallery',
    label: 'Gallery',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    to: '/admin/pages',
    label: 'Pages',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
  },
  {
    to: '/admin/invitations',
    label: 'Invitations',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    to: '/admin/join-requests',
    label: 'Join requests',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4v16m8-8H4"
        />
      </svg>
    ),
  },
  {
    to: '/admin/super',
    label: 'Super admin',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      </svg>
    ),
  },
];

/**
 * Sidebar navigation component
 */
function AdminSidebar() {
  const { user } = useAuth();
  const location = useLocation();

  /**
   * Check if the current path matches for NavLink active state
   */
  const isActiveLink = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <aside className="w-64 bg-secondary-dark border-r border-white/10 flex-shrink-0">
      {/* Admin profile section */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center">
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.displayName}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <span className="text-accent font-bold text-lg">
                {user?.displayName?.charAt(0).toUpperCase() || 'A'}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium truncate">{user?.displayName}</p>
            <p className="text-accent text-xs truncate font-medium">Super Admin</p>
          </div>
        </div>
      </div>

      {/* Navigation links */}
      <nav className="p-4">
        <p className="text-white/40 text-xs uppercase tracking-wider mb-3 px-4">
          Management
        </p>
        <ul className="space-y-2">
          {navItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === '/admin'}
                className={() =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActiveLink(item.to)
                      ? 'bg-accent/20 text-accent border border-accent/30'
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                {item.icon}
                <span className="font-medium">{item.label}</span>
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="ml-auto bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Quick links section */}
      <div className="p-4 mt-4 border-t border-white/10">
        <p className="text-white/40 text-xs uppercase tracking-wider mb-3 px-4">
          Quick Links
        </p>
        <ul className="space-y-1">
          <li>
            <Link
              to="/member/dashboard"
              className="flex items-center gap-3 px-4 py-2 text-white/60 hover:text-white transition-colors text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
              <span>Member Dashboard</span>
            </Link>
          </li>
          <li>
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-2 text-white/60 hover:text-white transition-colors text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                />
              </svg>
              <span>Public Site</span>
            </Link>
          </li>
        </ul>
      </div>

      {/* Bottom section with branding */}
      <div className="absolute bottom-0 left-0 w-64 p-6 border-t border-white/10 bg-secondary-dark">
        <div className="text-center">
          <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Admin Panel</p>
          <p className="text-accent font-medium text-sm">Re:Nav-96</p>
        </div>
      </div>
    </aside>
  );
}

/**
 * Mobile navigation bar for smaller screens
 */
function MobileNavBar() {
  const location = useLocation();

  const isActiveLink = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  // Show only first 5 most important items on mobile to avoid overflow
  const mobileNavItems = navItems.slice(0, 5);

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-secondary-dark border-t border-white/10">
      <div className="flex justify-around py-2">
        {mobileNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/admin'}
            className={() =>
              `flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
                isActiveLink(item.to) ? 'text-accent' : 'text-white/60'
              }`
            }
          >
            {item.icon}
            <span className="text-xs font-medium">{item.label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/**
 * Admin page header component
 */
function AdminHeader() {
  const location = useLocation();

  // Get current page title from path
  const getPageTitle = () => {
    const path = location.pathname;

    if (path === '/admin') {
      return 'Admin Dashboard';
    }
    if (path.startsWith('/admin/registrations')) {
      return 'Event Registrations';
    }
    if (path.startsWith('/admin/members')) {
      return 'Member Management';
    }
    if (path.startsWith('/admin/groups')) {
      return 'Groups & Folders';
    }
    if (path.startsWith('/admin/content')) {
      return 'Content Editor';
    }
    if (path.startsWith('/admin/photos')) {
      return 'Photo Approval';
    }
    if (path.includes('/surveys/') && path.includes('/results')) {
      return 'Survey Results';
    }
    if (path.startsWith('/admin/surveys')) {
      return 'Survey Builder';
    }

    return 'Admin';
  };

  return (
    <header className="bg-secondary-dark/50 border-b border-white/10 px-6 py-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-display font-bold text-white">{getPageTitle()}</h1>
        <div className="flex items-center gap-2">
          <span className="text-xs text-accent bg-accent/10 px-2 py-1 rounded border border-accent/20">
            Super Admin
          </span>
        </div>
      </div>
    </header>
  );
}

/**
 * AdminLayout Component
 *
 * Main layout wrapper for all admin pages. Provides:
 * - Sidebar navigation (desktop)
 * - Bottom navigation bar (mobile)
 * - Page header with current page title
 * - Content area with Outlet for child routes
 *
 * @returns The admin layout with sidebar and content area
 *
 * @example
 * // Used in router configuration
 * {
 *   path: 'admin',
 *   element: (
 *     <ProtectedRoute requiredRole="super_admin">
 *       <AdminLayout />
 *     </ProtectedRoute>
 *   ),
 *   children: [...]
 * }
 */
export function AdminLayout() {
  return (
    <div className="min-h-screen flex">
      {/* Desktop sidebar - hidden on mobile */}
      <div className="hidden md:flex relative">
        <AdminSidebar />
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col pb-16 md:pb-0">
        <AdminHeader />
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <MobileNavBar />
    </div>
  );
}

export default AdminLayout;
